import { HugeiconsIcon } from "@hugeicons/react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { memo } from "react";

import { cn } from "@/lib/utils";

import { glyphIconFor } from "./glyphs";
import type { CardNodeData } from "./types";

const HANDLE_CLASS = "!h-1.5 !w-1.5 !min-w-0 !border-0 !bg-transparent opacity-0";

/**
 * Custom React Flow node reproducing the `.card` from architecture-map.html:
 * left-accent border, glyph chip, title, subtitle, 2-line description, optional
 * code path, and a single tag pill.
 */
function CardNodeImpl({ data }: NodeProps) {
  const d = data as CardNodeData;
  const { entity, accent, dimmed } = d;
  const glyphIcon = entity.glyphIcon ?? glyphIconFor(entity.type);
  const subtitle = entity.subtitle ?? entity.type;
  const tag = entity.tag ?? entity.type;

  return (
    <div
      className={cn(
        "group w-[226px] min-h-[112px] cursor-grab select-none rounded-xl border border-border border-l-4 bg-card px-3 py-2.5 shadow-[var(--shadow-sm)] transition-opacity",
        "hover:shadow-[var(--shadow-md)] active:cursor-grabbing",
        dimmed && "opacity-[0.12]",
      )}
      style={{ borderLeftColor: accent }}
    >
      {/* hidden handles on all four sides so xyflow can attach edges */}
      <Handle type="target" position={Position.Left} className={HANDLE_CLASS} />
      <Handle type="target" position={Position.Top} className={HANDLE_CLASS} />
      <Handle type="source" position={Position.Right} className={HANDLE_CLASS} />
      <Handle type="source" position={Position.Bottom} className={HANDLE_CLASS} />

      <div className="flex items-center gap-1.5">
        <span
          className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded leading-none text-white"
          style={{ backgroundColor: accent }}
          aria-hidden
        >
          <HugeiconsIcon icon={glyphIcon} size={12} color="#fff" strokeWidth={2} />
        </span>
        <span className="truncate text-[12.5px] font-semibold text-card-foreground">
          {entity.title}
        </span>
      </div>

      <div className="mt-1 truncate text-[10.5px] text-muted-foreground">
        {subtitle}
      </div>

      {entity.description ? (
        <div className="mt-1 line-clamp-2 text-[10.8px] leading-snug text-muted-foreground">
          {entity.description}
        </div>
      ) : null}

      {entity.path ? (
        <code className="mt-1 block truncate text-[9.5px] text-muted-foreground/80">
          {entity.path}
        </code>
      ) : null}

      <div className="mt-1.5 flex">
        <span className="truncate rounded-full border border-border bg-muted px-1.5 py-px text-[9.5px] text-muted-foreground">
          {tag}
        </span>
      </div>
    </div>
  );
}

export const CardNode = memo(CardNodeImpl);
